import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody, TextField } from '@mui/material';
import apiService from '../services/api.service';

interface CompanyRow { CompanyID: number; CompanyName: string; }
interface ScheduleRow { scheduleid: number; name: string; companyid: number | null; endpointkey: string; selectedfields: string[]; datefield?: string | null; lastruntime?: string | null; }

const ItemMaster: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [scheduleId, setScheduleId] = useState<number | ''>('');
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState<string>('');

  const schedule = useMemo(() => schedules.find(s => s.scheduleid === scheduleId), [schedules, scheduleId]);
  const fields = schedule?.selectedfields || [];
  const lastRun = useMemo(() => (schedule?.lastruntime ? new Date(schedule!.lastruntime) : null), [schedule]);

  useEffect(() => {
    const init = async () => {
      const companiesRes = await apiService.getCompanies(true);
      const rawCompanies = Array.isArray(companiesRes) ? companiesRes : (companiesRes?.data || []);
      setCompanies(rawCompanies.map((c: any) => ({ CompanyID: c.CompanyID ?? c.companyid, CompanyName: c.CompanyName ?? c.companyname })));
      const schedulesRes = await apiService.listSchedules();
      setSchedules(schedulesRes.filter((s: any) => s.endpointkey === 'sap-items'));
    };
    init();
  }, []);

  useEffect(() => {
    if (!schedule) return;
    const df = schedule.datefield || '';
    if (df && lastRun) {
      const iso = lastRun.toISOString();
      const ymd = iso.slice(0,10);
      const isDateTime = /time|datetime/i.test(df);
      const isDateOnly = /date(?!time)/i.test(df);
      const literal = isDateTime ? `datetimeoffset'${iso}'` : (isDateOnly ? `datetime'${ymd}T00:00:00'` : `datetimeoffset'${iso}'`);
      setFilter(`${df} ge ${literal}`);
    } else {
      setFilter('');
    }
  }, [schedule, lastRun]);

  const load = async () => {
    if (!schedule) return;
    const data = await apiService.getItemsCustom(companyId === '' ? undefined : Number(companyId), fields, filter);
    const payload = Array.isArray(data) ? data : (data?.data || []);
    setRows(payload);
  };

  return (
    <Box component="section" aria-labelledby="im-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography
        component="h1"
        id="im-heading"
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
      >
        Item Master
      </Typography>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="company-select">Company</InputLabel>
              <Select labelId="company-select" value={companyId} label="Company" onChange={(e) => setCompanyId(e.target.value as any)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {companies.map(c => (<MenuItem key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="schedule-select">Schedule</InputLabel>
              <Select labelId="schedule-select" value={scheduleId} label="Schedule" onChange={(e) => setScheduleId(Number(e.target.value))}>
                <MenuItem value=""><em>None</em></MenuItem>
                {schedules.map(s => (<MenuItem key={s.scheduleid} value={s.scheduleid}>{s.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField label="Filter (SAP $filter)" fullWidth size="small" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={load} disabled={!schedule}>Load</Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {fields.map(f => (<TableCell key={f}>{f}</TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={idx}>
                {fields.map(f => (<TableCell key={f}>{String((r as any)[f] ?? '')}</TableCell>))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={Math.max(1, fields.length)}>No data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default ItemMaster;